import { useState } from 'react';
import { useLoaderData } from 'react-router';

import { Badge, Card } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import type { CargaDaPlataforma, UsuarioNaPlataforma } from '@/data/tipos';
import { EquipeDaPlataforma } from './EquipeDaPlataforma';
import { quandoRelativo } from './Visao';

const ACESSO: Record<UsuarioNaPlataforma['acesso'], { rotulo: string; tom: 'success' | 'warning' | 'neutral' }> = {
    'ativo': { rotulo: 'Ativo', tom: 'success' },
    'convite-pendente': { rotulo: 'Convite pendente', tom: 'warning' },
    'desativado': { rotulo: 'Desativado', tom: 'neutral' },
};

function semAcento(v: string): string {
    return v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

type Aba = 'clinicas' | 'equipe';

export function Usuarios() {
    const carga = useLoaderData() as CargaDaPlataforma;
    const [aba, setAba] = useState<Aba>('clinicas');
    const [busca, setBusca] = useState('');

    const lista = carga.usuarios.filter((u) => !busca
        || semAcento(u.nome).includes(semAcento(busca))
        || semAcento(u.email).includes(semAcento(busca))
        || semAcento(u.clinica).includes(semAcento(busca)));

    const colunas: DataTableColumn<UsuarioNaPlataforma>[] = [
        {
            key: 'nome', header: 'Pessoa', sortable: true,
            render: (u) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{u.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{u.email || 'sem e-mail cadastrado'}</span>
                </span>
            ),
        },
        { key: 'clinica', header: 'Clínica', sortable: true },
        { key: 'papel', header: 'Papel', width: 140 },
        {
            key: 'acesso', header: 'Acesso', width: 170,
            render: (u) => <Badge tone={ACESSO[u.acesso].tom} dot>{ACESSO[u.acesso].rotulo}</Badge>,
        },
        {
            key: 'temLogin', header: 'Login', width: 110,
            /* Membro sem login é convite que ninguém aceitou — e é diferente de
               conta desativada, que já existiu. */
            render: (u) => (u.temLogin ? 'sim' : <span style={{ color: 'var(--text-tertiary)' }}>não criado</span>),
        },
        {
            key: 'ultimoAcesso', header: 'Último acesso', align: 'right', width: 140,
            render: (u) => quandoRelativo(u.ultimoAcesso),
        },
    ];

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Usuários e acessos"
                description="Quem tem conta em cada clínica, e quem opera a plataforma."
            >
                <Tabs
                    ariaLabel="Quem" activeId={aba}
                    onChange={(id) => setAba(id === 'equipe' ? 'equipe' : 'clinicas')}
                    tabs={[
                        { id: 'clinicas', label: 'Contas das clínicas', count: carga.usuarios.length },
                        { id: 'equipe', label: 'Equipe da plataforma' },
                    ]}
                />
            </PageHeader>

            {aba === 'clinicas' && (
                <>
                    {/* Mudar o acesso de alguém é ato DENTRO da clínica, e a plataforma
                        não entra sem impersonação. Listar é uma coisa; operar é outra. */}
                    <Alert tone="info" dismissible={false} title="Aqui só se lê">
                        Ativar, desativar ou trocar o papel de uma pessoa é operação da clínica — quem faz isso é a
                        própria equipe, em Configurações › Equipe. Da plataforma, o caminho é a impersonação, que fica
                        registrada.
                    </Alert>

                    <FilterBar
                        search={<SearchField value={busca} onChange={setBusca} placeholder="Buscar por pessoa, e-mail ou clínica" width={300} />}
                        resultLabel={`${lista.length} ${lista.length === 1 ? 'pessoa' : 'pessoas'}`}
                    />

                    <Card padding={0}>
                        <DataTable
                            columns={colunas} rows={lista} rowKey="id"
                            stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            emptyMessage="Nenhuma pessoa encontrada."
                        />
                    </Card>
                </>
            )}

            {aba === 'equipe' && <EquipeDaPlataforma />}
        </div>
    );
}
